import { FC, useMemo } from 'react';

import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { useAuth } from '@shared/state/hooks/useAuth';

import { BillingCardSection } from './couponDetail/BillingCardSection';
import { CancelConfirmDialog } from './couponDetail/CancelConfirmDialog';
import { CancelSection } from './couponDetail/CancelSection';
import { CouponDetailHeader } from './couponDetail/CouponDetailHeader';
import { CouponShareHistoryModal } from './couponDetail/CouponShareHistoryModal';
import { CouponTable } from './couponDetail/CouponTable';
import { CourtesyMessageModal } from './couponDetail/CourtesyMessageModal';
import { CourtesySection } from './couponDetail/CourtesySection';
import { CrossCouponOverlapBanner } from './couponDetail/CrossCouponOverlapBanner';
import { DisposeConfirmDialog } from './couponDetail/DisposeConfirmDialog';
import { DisposeSection } from './couponDetail/DisposeSection';
import { ExtraRedeemModal } from './couponDetail/ExtraRedeemModal';
import { ExtraFulfillModal, ExtraPayModal } from './ExtraPaymentActionModals';
import { KakaoShareCallbackModal } from './couponDetail/KakaoShareCallbackModal';
import { ReissueSection } from './couponDetail/ReissueSection';
import { ResultBanners } from './couponDetail/ResultBanners';
import { CouponDetailModalProps } from './couponDetail/types';
import { useCouponDetailState } from './couponDetail/useCouponDetailState';

export type { CouponDetailModalProps };

export const CouponDetailModal: FC<CouponDetailModalProps> = (props) => {
  const { onClose } = props;
  const { isAdmin, isGiftAgent } = useAuth();
  const state = useCouponDetailState(props);
  const sortedCoupons = state.sortedCoupons;
  const couponMap = useMemo(() => {
    const map = new Map<string, GiftCouponSummary>();
    for (const c of sortedCoupons) map.set(c._id, c);
    return map;
  }, [sortedCoupons]);
  const canSendCourtesy = isAdmin() || isGiftAgent();
  const courtesySelectedCoupons = useMemo(
    () =>
      Array.from(state.courtesySelectedIds)
        .map((id) => couponMap.get(id))
        .filter((c): c is GiftCouponSummary => !!c),
    [state.courtesySelectedIds, couponMap],
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-[99vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <CouponDetailHeader
          stats={state.stats}
          orderId={props.orderId}
          order={props.order}
          previewCopied={state.previewCopied}
          setPreviewCopied={state.setPreviewCopied}
          onClose={onClose}
        />

        <ResultBanners
          cancelResult={state.cancelResult}
          additionalResult={state.additionalResult}
          disposeResult={state.disposeResult}
          refundResult={state.refundResult}
        />

        <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
          <CrossCouponOverlapBanner
            overlap={state.crossCouponHashOverlap}
            couponMap={couponMap}
            giftItemEmbedMap={state.giftItemEmbedMap}
            onOpenKakaoShareCallbacks={state.openKakaoCallbackModal}
            onOpenShareHistory={state.openShareHistoryModal}
          />

          <div className="mb-3 grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
            {state.canCancel && state.cancellableCoupons.length > 0 && (
              <CancelSection
                cancellableCoupons={state.cancellableCoupons}
                selectedIds={state.selectedIds}
                isFullCancel={state.isFullCancel}
                toggleSelectAll={state.toggleSelectAll}
                setShowCancelConfirm={state.setShowCancelConfirm}
              />
            )}

            {state.canCancel && state.reissueStatsByItem.length > 0 && (
              <ReissueSection
                reissueStatsByItem={state.reissueStatsByItem}
                totalReissueRemaining={state.totalReissueRemaining}
                showAdditionalForm={state.showAdditionalForm}
                setShowAdditionalForm={state.setShowAdditionalForm}
                reissueGiftItemId={state.reissueGiftItemId}
                setReissueGiftItemId={state.setReissueGiftItemId}
                additionalCount={state.additionalCount}
                setAdditionalCount={state.setAdditionalCount}
                selectedReissueStat={state.selectedReissueStat}
                isCreatingAdditional={state.isCreatingAdditional}
                handleCreateAdditional={state.handleCreateAdditional}
                reissueOverride={state.reissueOverride}
                requestReissueOverride={state.requestReissueOverride}
              />
            )}

            {state.canCancel && (
              <DisposeSection
                disposeMode={state.disposeMode}
                setDisposeMode={state.setDisposeMode}
                disposeAction={state.disposeAction}
                setDisposeAction={state.setDisposeAction}
                disposeSelectedIds={state.disposeSelectedIds}
                setDisposeSelectedIds={state.setDisposeSelectedIds}
                setShowDisposeConfirm={state.setShowDisposeConfirm}
                disposableCoupons={state.disposableCoupons}
                toggleDisposeSelectAll={state.toggleDisposeSelectAll}
              />
            )}

            {isAdmin() && props.orderId && (
              <BillingCardSection orderId={props.orderId} />
            )}

            {canSendCourtesy && state.courtesyEligibleCoupons.length > 0 && (
              <CourtesySection
                courtesyMode={state.courtesyMode}
                setCourtesyMode={state.setCourtesyMode}
                courtesyEligibleCoupons={state.courtesyEligibleCoupons}
                courtesySelectedIds={state.courtesySelectedIds}
                setCourtesySelectedIds={state.setCourtesySelectedIds}
                toggleCourtesySelectAll={state.toggleCourtesySelectAll}
                setShowCourtesyModal={state.setShowCourtesyModal}
              />
            )}
          </div>

          <CouponTable
            sortedCoupons={state.sortedCoupons}
            giftItemEmbedMap={state.giftItemEmbedMap}
            canCancel={state.canCancel}
            hasCancellableCoupons={state.cancellableCoupons.length > 0}
            disposeMode={state.disposeMode}
            courtesyMode={state.courtesyMode}
            selectedIds={state.selectedIds}
            disposeSelectedIds={state.disposeSelectedIds}
            courtesySelectedIds={state.courtesySelectedIds}
            isPromoting={state.isPromoting}
            crossCouponOverlapIds={state.crossCouponHashOverlap.overlapCouponIds}
            toggleSelect={state.toggleSelect}
            toggleDisposeSelect={state.toggleDisposeSelect}
            toggleCourtesySelect={state.toggleCourtesySelect}
            onOpenShareHistory={state.openShareHistoryModal}
            handlePromoteToReissued={state.handlePromoteToReissued}
            onIssueRefund={state.handleIssueRefund}
            onIssueRenewal={state.handleIssueRenewal}
            onCancelIssue={state.handleCancelIssue}
            onOpenKakaoShareCallbacks={state.openKakaoCallbackModal}
            isIssuing={state.isIssuing}
            onResetShareStatus={state.handleResetShareStatus}
            isResettingShare={state.isResettingShare}
            onUpdateAdditionalPhone={state.handleUpdateAdditionalPhone}
            onOpenExtraRedeem={state.openExtraRedeemModal}
            onSpawnPay={state.openSpawnPayModal}
            onSpawnFulfill={state.openSpawnFulfillModal}
            onSpawnCancel={state.handleCancelSpawn}
            cancelingSpawnId={state.cancelingSpawnId}
          />
        </div>

        {state.showDisposeConfirm && (
          <DisposeConfirmDialog
            disposeAction={state.disposeAction}
            disposeSelectedIds={state.disposeSelectedIds}
            disposeReason={state.disposeReason}
            setDisposeReason={state.setDisposeReason}
            isDisposing={state.isDisposing}
            setShowDisposeConfirm={state.setShowDisposeConfirm}
            handleDispose={state.handleDispose}
          />
        )}

        {state.showCancelConfirm && (
          <CancelConfirmDialog
            isFullCancel={state.isFullCancel}
            hasDeliveryItems={state.hasDeliveryItems}
            selectedIds={state.selectedIds}
            cancelReason={state.cancelReason}
            setCancelReason={state.setCancelReason}
            isCancelling={state.isCancelling}
            setShowCancelConfirm={state.setShowCancelConfirm}
            handleCancelRequest={state.handleCancelRequest}
          />
        )}
      </div>

      {state.showCourtesyModal && (
        <CourtesyMessageModal
          coupons={courtesySelectedCoupons}
          onClose={() => state.setShowCourtesyModal(false)}
          onSent={() => {
            state.setShowCourtesyModal(false);
            state.setCourtesyMode(false);
            state.setCourtesySelectedIds(new Set());
          }}
        />
      )}

      {state.kakaoCallbackCoupon && (
        <KakaoShareCallbackModal
          coupon={state.kakaoCallbackCoupon}
          onClose={state.closeKakaoCallbackModal}
        />
      )}

      {state.shareHistoryCoupon && (
        <CouponShareHistoryModal
          coupon={state.shareHistoryCoupon}
          onClose={state.closeShareHistoryModal}
        />
      )}

      {state.extraRedeemCoupon && (
        <ExtraRedeemModal
          coupon={state.extraRedeemCoupon}
          onClose={state.closeExtraRedeemModal}
          onChanged={() => window.location.reload()}
        />
      )}

      {state.spawnPayCoupon && (
        <ExtraPayModal
          couponId={state.spawnPayCoupon._id}
          couponCode={state.spawnPayCoupon.code}
          productLabel={
            state.giftItemEmbedMap.get(state.spawnPayCoupon.giftItem)?.name ??
            state.spawnPayCoupon.code
          }
          onPay={state.handleSpawnPay}
          onClose={state.closeSpawnPayModal}
        />
      )}

      {state.spawnFulfillCoupon && (
        <ExtraFulfillModal
          couponId={state.spawnFulfillCoupon._id}
          couponCode={state.spawnFulfillCoupon.code}
          productLabel={
            state.giftItemEmbedMap.get(state.spawnFulfillCoupon.giftItem)?.name ??
            state.spawnFulfillCoupon.code
          }
          getDonors={state.getDonorCandidates}
          onFulfill={state.handleSpawnFulfill}
          onClose={state.closeSpawnFulfillModal}
        />
      )}
    </div>
  );
};
